import type { SdkTransport, WorkflowJobHandle } from '@picsart/ai-sdk';
import { createClient, catalog, Model } from '@picsart/ai-sdk';
import { getContext } from 'configs/context';
import { apiBaseUrl } from 'configs/constants';
import { ApiError } from 'utils/apiError';

interface TransportRequest {
  workflow: string;
  payload: unknown;
  signal?: AbortSignal;
}

function parseError(body: unknown): { message?: string; reason?: string } {
  if (!body || typeof body !== 'object') return {};
  const obj = body as Record<string, unknown>;
  const reason = typeof obj.reason === 'string' ? obj.reason : undefined;
  const message = typeof obj.message === 'string' ? obj.message : typeof obj.error === 'string' ? obj.error : reason;
  return { message, reason };
}

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

// Map non-ok responses to ApiError so the auth/credits handling in the AI Background
// hooks keeps working when generating via the SDK (no more monetization.pending gate).
async function throwForNonOk(response: Response, fallback: string): Promise<void> {
  if (response.ok) return;
  const payload = await readJsonSafe(response);
  const { message, reason } = parseError(payload);
  throw new ApiError(`${fallback} (${response.status})${message ? `: ${message}` : ''}`, reason ?? 'unknown', response.status);
}

function parseTaskId(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const obj = payload as Record<string, unknown>;
  const { response } = obj;
  if (response && typeof response === 'object') {
    const { id } = response as Record<string, unknown>;
    if (typeof id === 'string') return id;
  }
  if (typeof obj.id === 'string') return obj.id;
  return undefined;
}

// Transport over the Picsart workflows backend, wrapping the miniapp's authenticated
// fetch: POST /workflows/{wf}/{submit|execute} with { params } + GET /workflows/{wf}/{id}/result.
const transport: SdkTransport = {
  submit: async request => {
    const { workflow, payload, signal } = request as TransportRequest;
    const response = await getContext().utils.api.fetch(`${apiBaseUrl}/workflows/${workflow}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ params: payload }),
      signal,
    });
    await throwForNonOk(response, `Workflow submit failed for ${workflow}`);
    const body = await readJsonSafe(response);
    const id = parseTaskId(body);
    if (!id) {
      throw new Error(`Workflow submit failed for ${workflow}: missing task id`);
    }
    return { workflow, id } as WorkflowJobHandle;
  },

  status: async (handle, signal) => {
    const response = await getContext().utils.api.fetch(`${apiBaseUrl}/workflows/${handle.workflow}/${handle.id}/result`, { method: 'GET', signal });
    await throwForNonOk(response, `Workflow status failed for ${handle.workflow}:${handle.id}`);
    return readJsonSafe(response);
  },

  execute: async request => {
    const { workflow, payload, signal } = request as TransportRequest;
    const response = await getContext().utils.api.fetch(`${apiBaseUrl}/workflows/${workflow}/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ params: payload }),
      signal,
    });
    await throwForNonOk(response, `Workflow execute failed for ${workflow}`);
    return readJsonSafe(response);
  },
};

/** ai-sdk model backing AI Background generation + pricing. */
export const AI_BACKGROUND_MODEL = 'picsart-change-bg';

/** Shared @picsart/ai-sdk client used for AI Background generation (picsart-change-bg). */
export const client = createClient(transport);

let pricingConfigured = false;

/** Point the SDK pricing source at the Picsart model-pricing service (idempotent). */
export function configurePricing(): void {
  if (pricingConfigured) return;
  catalog.pricing.configure({
    baseUrl: apiBaseUrl as string,
    fetch: (input, init) => getContext().utils.api.fetch(String(input), init as RequestInit),
  });
  pricingConfigured = true;
}

export const loadPricing = (): Promise<void> => catalog.pricing.load();
export const isPricingLoaded = (): boolean => catalog.pricing.isLoaded();

/**
 * Per-image credit price (min of the credit range) for a model, or null if unavailable. Synchronous —
 * assumes pricing is already loaded (see loadPricing / useModelPrice). Pass an SDK catalog model id
 * (resolve any alias at the call site).
 */
export function getModelCreditPrice(modelId: string): number | null {
  try {
    return Model(modelId).getCreditsInfo()?.min ?? null;
  } catch {
    return null;
  }
}
